import React from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Upload,
  Users,
  FolderOpen,
  LogOut,
  GraduationCap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/upload", icon: Upload, label: "Data Upload" },
  { path: "/students", icon: GraduationCap, label: "Students" },
  { path: "/mentors", icon: Users, label: "Mentors" },
  { path: "/resources", icon: FolderOpen, label: "Resources" },
];

export default function DashboardLayout({ onLogout }) {
  const location = useLocation();

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 z-40 h-screen w-64 border-r border-gray-200 bg-white hidden md:block" style={{ boxShadow: '1px 0 12px rgba(0,0,0,0.06)' }}>
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex items-center px-5 py-5 border-b border-gray-200">
            <img
              src="/mu-logo.jpeg"
              alt="Masters' Union"
              className="w-44 object-contain"
              data-testid="sidebar-logo"
            />
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3 py-4">
            <nav className="space-y-1">
              {navItems.map((item) => {
                const isActive =
                  item.path === "/"
                    ? location.pathname === "/"
                    : location.pathname.startsWith(item.path);

                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? "bg-[#E8B923]/8 text-[#8B6F47] font-semibold border border-[#E8B923]/20"
                        : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>
          </ScrollArea>

          {/* Logout */}
          <div className="border-t border-gray-200 p-4">
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-gray-500 hover:bg-red-50 hover:text-red-600"
              onClick={onLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 z-30 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 md:hidden">
        <div className="flex items-center gap-3">
          <img
            src="/mu-logo.jpeg"
            alt="Masters' Union"
            className="h-8 object-contain"
          />
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="text-gray-500 hover:text-red-600"
          onClick={onLogout}
          data-testid="mobile-logout-btn"
        >
          <LogOut className="h-5 w-5" />
        </Button>
      </header>

      {/* Mobile Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 flex items-center justify-around border-t border-gray-200 bg-white py-2 md:hidden">
        {navItems.slice(0, 5).map((item) => {
          const isActive =
            item.path === "/"
              ? location.pathname === "/"
              : location.pathname.startsWith(item.path);

          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center gap-1 p-2 ${
                isActive ? "text-[#B8860B]" : "text-gray-400"
              }`}
            >
              <item.icon className="h-5 w-5" />
              <span className="text-[10px]">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* Main Content */}
      <main className="min-h-screen bg-[#FAFAFA] md:pl-64 pt-14 md:pt-0 pb-20 md:pb-0">
        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
